// Chat wallpaper presets + user-uploaded custom wallpapers.
// Everything is stored per-device in localStorage. Custom uploads are
// compressed via canvas before persisting so we don't blow the ~5MB LS quota.

import { useEffect, useState } from "react";

const LS_KEY = "notelosh_wallpaper";
const LS_CUSTOM_KEY = "notelosh_wallpaper_custom";
const LS_FIT_KEY = "notelosh_wallpaper_fit"; // "contain" (default) | "cover"
export const WALLPAPER_EVENT = "notelosh:wallpaper-changed";
export const WALLPAPER_FIT_EVENT = "notelosh:wallpaper-fit-changed";
export const WALLPAPER_POSITION_EVENT = "notelosh:wallpaper-position-changed";

const CUSTOM_MAX_WIDTH = 2400;
const CUSTOM_JPEG_QUALITY = 0.88;
export const CUSTOM_WALLPAPER_LIMIT = 6;
export const CUSTOM_INPUT_MAX_BYTES = 15 * 1024 * 1024; // pre-compression cap

export const WALLPAPERS = [
  {
    id: "none",
    name: "Default",
    style: { background: "#0A0A0A" },
    thumb: { background: "#0A0A0A" },
  },
  {
    id: "dots",
    name: "Dots",
    style: {
      backgroundColor: "#0A0A0A",
      backgroundImage: "radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1px)",
      backgroundSize: "18px 18px",
    },
    thumb: {
      backgroundColor: "#0A0A0A",
      backgroundImage: "radial-gradient(rgba(255,255,255,0.18) 1.5px, transparent 1.5px)",
      backgroundSize: "8px 8px",
    },
  },
  {
    id: "grid",
    name: "Grid",
    style: {
      backgroundColor: "#0A0A0A",
      backgroundImage:
        "linear-gradient(rgba(255,255,255,0.045) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.045) 1px, transparent 1px)",
      backgroundSize: "32px 32px",
    },
    thumb: {
      backgroundColor: "#0A0A0A",
      backgroundImage:
        "linear-gradient(rgba(255,255,255,0.18) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.18) 1px, transparent 1px)",
      backgroundSize: "10px 10px",
    },
  },
  {
    id: "aurora",
    name: "Aurora",
    style: {
      background:
        "radial-gradient(1200px 600px at 15% 10%, rgba(37,99,235,0.18), transparent 60%), radial-gradient(900px 500px at 85% 90%, rgba(139,92,246,0.16), transparent 60%), #0A0A0A",
    },
    thumb: {
      background:
        "radial-gradient(circle at 25% 25%, rgba(37,99,235,0.9), transparent 60%), radial-gradient(circle at 75% 75%, rgba(139,92,246,0.9), transparent 60%), #0A0A0A",
    },
  },
  {
    id: "sunset",
    name: "Sunset",
    style: {
      background:
        "radial-gradient(900px 500px at 90% 10%, rgba(245,158,11,0.18), transparent 60%), radial-gradient(1100px 600px at 10% 100%, rgba(236,72,153,0.14), transparent 60%), #0A0A0A",
    },
    thumb: {
      background:
        "radial-gradient(circle at 70% 30%, rgba(245,158,11,0.9), transparent 60%), radial-gradient(circle at 30% 70%, rgba(236,72,153,0.9), transparent 60%), #0A0A0A",
    },
  },
  {
    id: "forest",
    name: "Forest",
    style: {
      background:
        "radial-gradient(1000px 550px at 20% 20%, rgba(16,185,129,0.15), transparent 60%), radial-gradient(900px 500px at 80% 80%, rgba(6,95,70,0.30), transparent 60%), #0A0A0A",
    },
    thumb: {
      background:
        "radial-gradient(circle at 30% 30%, rgba(16,185,129,0.9), transparent 60%), radial-gradient(circle at 70% 70%, rgba(6,95,70,0.9), transparent 60%), #0A0A0A",
    },
  },
  {
    id: "paper",
    name: "Slate",
    style: {
      backgroundColor: "#0F1116",
      backgroundImage:
        "repeating-linear-gradient(45deg, rgba(255,255,255,0.02) 0 2px, transparent 2px 6px)",
    },
    thumb: {
      backgroundColor: "#0F1116",
      backgroundImage:
        "repeating-linear-gradient(45deg, rgba(255,255,255,0.12) 0 2px, transparent 2px 6px)",
    },
  },
];

export const DEFAULT_WALLPAPER_ID = "none";

// -------- Custom (user-uploaded) wallpapers ---------------------------------

/** Return [{id, url}] of user uploads (data URLs). */
export function getCustomWallpapers() {
  try {
    const raw = localStorage.getItem(LS_CUSTOM_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

/**
 * Persist the customs list. Throws a clear error if the device localStorage
 * quota is exhausted (common when a data-URL pushes the whole domain over
 * ~5 MB). Callers are expected to catch that and retry with a smaller image.
 */
function saveCustomWallpapers(list) {
  try {
    localStorage.setItem(LS_CUSTOM_KEY, JSON.stringify(list));
  } catch (e) {
    const err = new Error("Device storage is full. Remove a wallpaper or pick a smaller image.");
    err.code = "quota";
    err.cause = e;
    throw err;
  }
}

/**
 * Read a user-picked image file and try to store it. If the compressed
 * result is too big for localStorage we automatically retry at progressively
 * smaller max-widths and JPEG qualities before giving up. Returns the new
 * entry with a fresh id.
 */
export async function addCustomWallpaper(file) {
  if (!file) throw new Error("No file");
  if (!file.type?.startsWith("image/")) throw new Error("Please pick an image file");
  if (file.size > CUSTOM_INPUT_MAX_BYTES) throw new Error("Image too large (max 15 MB)");

  const existing = getCustomWallpapers();
  if (existing.length >= CUSTOM_WALLPAPER_LIMIT) {
    throw new Error(`You can only keep ${CUSTOM_WALLPAPER_LIMIT} custom wallpapers. Remove one first.`);
  }

  // Try increasingly-aggressive compression until it fits in localStorage.
  const attempts = [
    { maxWidth: CUSTOM_MAX_WIDTH, quality: CUSTOM_JPEG_QUALITY }, // 2400 @ 0.88
    { maxWidth: 1920, quality: 0.82 },
    { maxWidth: 1600, quality: 0.78 },
    { maxWidth: 1280, quality: 0.72 },
    { maxWidth: 1024, quality: 0.68 },
  ];
  let lastQuotaErr = null;
  for (const opts of attempts) {
    const dataUrl = await compressImageToDataUrl(file, opts.maxWidth, opts.quality);
    const entry = {
      id: `custom_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
      url: dataUrl,
    };
    try {
      saveCustomWallpapers([...existing, entry]);
      return entry;
    } catch (e) {
      if (e?.code !== "quota") throw e;
      lastQuotaErr = e;
      // Continue to the next, smaller attempt.
    }
  }
  throw lastQuotaErr || new Error("Could not save wallpaper");
}

export function removeCustomWallpaper(id) {
  const next = getCustomWallpapers().filter((w) => w.id !== id);
  saveCustomWallpapers(next);
  // If the deleted wallpaper was the active one, fall back to default.
  if (getWallpaperId() === id) setWallpaperId(DEFAULT_WALLPAPER_ID);
}

function compressImageToDataUrl(file, maxWidth = CUSTOM_MAX_WIDTH, quality = CUSTOM_JPEG_QUALITY) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try {
        const scale = img.width > maxWidth ? maxWidth / img.width : 1;
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        const dataUrl = canvas.toDataURL("image/jpeg", quality);
        URL.revokeObjectURL(url);
        resolve(dataUrl);
      } catch (e) {
        URL.revokeObjectURL(url);
        reject(e);
      }
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Could not read image")); };
    img.src = url;
  });
}

// -------- Selection ---------------------------------------------------------

export function getWallpaperId() {
  try { return localStorage.getItem(LS_KEY) || DEFAULT_WALLPAPER_ID; }
  catch { return DEFAULT_WALLPAPER_ID; }
}

/** "contain" (default — full picture, letterboxed if needed) or "cover" (fills screen, may crop). */
export function getWallpaperFit() {
  try {
    const v = localStorage.getItem(LS_FIT_KEY);
    return v === "cover" ? "cover" : "contain";
  } catch { return "contain"; }
}

export function setWallpaperFit(fit) {
  try {
    if (fit === "cover") localStorage.setItem(LS_FIT_KEY, "cover");
    else localStorage.removeItem(LS_FIT_KEY);
  } catch { /* ignore */ }
  window.dispatchEvent(new CustomEvent(WALLPAPER_FIT_EVENT, { detail: { fit } }));
}

/** Returns {x, y} in 0..100 percentages for `object-position`. Center by default. */
export function getWallpaperPosition(id) {
  const c = getCustomWallpapers().find((w) => w.id === id);
  const p = c?.position;
  if (!p || typeof p.x !== "number" || typeof p.y !== "number") return { x: 50, y: 50 };
  return { x: clamp01(p.x), y: clamp01(p.y) };
}

/** Persists the crop position for a custom wallpaper and notifies listeners. */
export function setWallpaperPosition(id, { x, y }) {
  const list = getCustomWallpapers();
  const idx = list.findIndex((w) => w.id === id);
  if (idx < 0) return;
  list[idx] = { ...list[idx], position: { x: clamp01(x), y: clamp01(y) } };
  saveCustomWallpapers(list);
  window.dispatchEvent(new CustomEvent(WALLPAPER_POSITION_EVENT, { detail: { id, x, y } }));
}

function clamp01(v) { return Math.max(0, Math.min(100, Number(v) || 0)); }

/** Resolve the active wallpaper (preset OR custom) into a {id, name, kind, ...} shape. */
export function getWallpaper() {
  const id = getWallpaperId();
  const preset = WALLPAPERS.find((w) => w.id === id);
  if (preset) return { ...preset, kind: "preset" };
  const custom = getCustomWallpapers().find((w) => w.id === id);
  if (custom) {
    return {
      id: custom.id,
      name: "Custom",
      kind: "custom",
      url: custom.url,
      position: custom.position || { x: 50, y: 50 },
      thumb: { backgroundImage: `url("${custom.url}")`, backgroundSize: "cover", backgroundPosition: "center" },
    };
  }
  return { ...WALLPAPERS[0], kind: "preset" };
}

export function setWallpaperId(id) {
  try {
    if (id && id !== DEFAULT_WALLPAPER_ID) localStorage.setItem(LS_KEY, id);
    else localStorage.removeItem(LS_KEY);
  } catch { /* ignore */ }
  window.dispatchEvent(new CustomEvent(WALLPAPER_EVENT, { detail: { id } }));
}

/**
 * Bulk-apply server-side wallpaper settings to this device. Called by
 * WallpaperSync on sign-in with the payload from GET /user/settings. Only
 * fields present in `settings` are applied — undefined leaves them alone.
 */
export function applyRemoteWallpaperSettings(settings) {
  if (!settings) return;
  if (Array.isArray(settings.custom_wallpapers)) {
    // Server is the source of truth for the customs list. Fall back to a
    // local write; if quota fails, we silently skip (localStorage stays as-is).
    try { saveCustomWallpapers(settings.custom_wallpapers); }
    catch { /* device out of storage — remote sync will retry from other side */ }
  }
  if (typeof settings.wallpaper_fit === "string") {
    try {
      if (settings.wallpaper_fit === "cover") localStorage.setItem(LS_FIT_KEY, "cover");
      else localStorage.removeItem(LS_FIT_KEY);
    } catch { /* ignore */ }
    window.dispatchEvent(new CustomEvent(WALLPAPER_FIT_EVENT, { detail: { fit: settings.wallpaper_fit } }));
  }
  if (settings.wallpaper_id !== undefined) {
    setWallpaperId(settings.wallpaper_id || DEFAULT_WALLPAPER_ID);
  } else {
    // Wallpapers list changed but id didn't — still fire an event so the
    // backdrop re-renders with the new set.
    window.dispatchEvent(new CustomEvent(WALLPAPER_EVENT, { detail: { id: getWallpaperId() } }));
  }
}

/**
 * React hook that returns the currently active wallpaper AND its fit mode.
 * Re-renders whenever either changes.
 */
export function useActiveWallpaper() {
  const [state, setState] = useState(() => ({ wallpaper: getWallpaper(), fit: getWallpaperFit() }));
  useEffect(() => {
    const refresh = () => setState({ wallpaper: getWallpaper(), fit: getWallpaperFit() });
    window.addEventListener(WALLPAPER_EVENT, refresh);
    window.addEventListener(WALLPAPER_FIT_EVENT, refresh);
    window.addEventListener(WALLPAPER_POSITION_EVENT, refresh);
    return () => {
      window.removeEventListener(WALLPAPER_EVENT, refresh);
      window.removeEventListener(WALLPAPER_FIT_EVENT, refresh);
      window.removeEventListener(WALLPAPER_POSITION_EVENT, refresh);
    };
  }, []);
  return state;
}

/**
 * Apply a preset (gradient/pattern) wallpaper to `document.body`. Custom
 * wallpapers use a real DOM layer (<WallpaperBackdrop />) instead, because
 * `background-attachment: fixed` + `background-size: contain/cover` glitches
 * on Android WebView when the keyboard opens or safe-area insets change.
 */
export function useWallpaperOnBody() {
  useEffect(() => {
    const apply = () => {
      const w = getWallpaper();
      const b = document.body.style;
      b.background = ""; b.backgroundColor = ""; b.backgroundImage = "";
      b.backgroundSize = ""; b.backgroundPosition = ""; b.backgroundRepeat = "";
      b.backgroundAttachment = "";
      if (w.kind === "custom") {
        // WallpaperBackdrop (portalled to body) draws the image; keep body
        // transparent so the backdrop shows through the React root.
        b.backgroundColor = "transparent";
      } else {
        Object.assign(b, w.style || {});
        b.backgroundAttachment = "fixed";
      }
    };
    apply();
    window.addEventListener(WALLPAPER_EVENT, apply);
    window.addEventListener(WALLPAPER_FIT_EVENT, apply);
    window.addEventListener(WALLPAPER_POSITION_EVENT, apply);
    return () => {
      window.removeEventListener(WALLPAPER_EVENT, apply);
      window.removeEventListener(WALLPAPER_FIT_EVENT, apply);
      window.removeEventListener(WALLPAPER_POSITION_EVENT, apply);
    };
  }, []);
}
